import { Component, computed, effect, ElementRef, inject, input, signal } from '@angular/core';
import { TooltipOptions } from 'primeng/api';
import { tap, take } from 'rxjs/operators';
import { CommandInput, CommandTemplate } from '../../entities/commandTemplate/commandTemplate';
import { ApolloService } from '../../services/apollo.service';
import { CommandService, HasCommands, Reloadable } from '../../services/command/command.service';
import * as fromGraphQl from './graphql';

@Component({
  selector: "nx-command-button",
  templateUrl: "command-button.html",
  host: {
    'class': 'nx-command-button'
  },
  styleUrls: ["./command-button.scss"]
})
export class CommandButtonComponent {

  private readonly elRef = inject(ElementRef);
  private readonly apolloService = inject(ApolloService);
  private readonly commandService = inject(CommandService);

  // Signals for inputs
  readonly commandName = input<string>();
  readonly commandInput = input<CommandInput>();
  readonly hasCommands = input<HasCommands>();
  readonly icon = input<string>("icon-plus");
  readonly tooltip = input<string>();
  readonly tooltipPosition = input<string>("bottom");
  readonly style = input<"light" | "transparent" | "dark">("transparent");
  readonly width = input<'auto' | 'full'>('auto');
  readonly iconPos = input<"left" | "right">("left");
  readonly reloadable = input<Reloadable>();
  readonly iconOnly = input<boolean>(false);

  // Writable signal for commandTemplate (can be set via input or loaded)
  readonly commandTemplate = signal<CommandTemplate | undefined>(undefined);

  readonly tooltipOptions: TooltipOptions = {
    hideDelay: 150,
    showDelay: 300,
    tooltipEvent: 'hover',
    tooltipPosition: 'bottom'
  };

  // Computed signals for template bindings (avoid function calls in template)
  readonly buttonClasses = computed(() => {
    let classes = "";
    if (this.icon() === undefined) {
      classes = "p-button-text-only";
    } else {
      classes = "p-button-text-and-icon";
    }
    if (this.width() === 'full') {
      classes += " p-button-full-width";
    }
    return classes;
  });

  readonly iconPosClass = computed(() => {
    if (this.icon() === undefined) {
      return undefined;
    }
    return this.iconPos();
  });

  readonly additionalClasses = computed(() => {
    let additionalClasses = this.style();
    const iconClass = this.iconPosClass();
    if (iconClass !== undefined) {
      additionalClasses += " icon-" + iconClass;
    }
    return additionalClasses;
  });

  readonly isDisabled = computed(() => {
    const template = this.commandTemplate();
    if (template?.settings?.disabled !== undefined) {
      return template.settings.disabled;
    }
    return true;
  });

  readonly buttonLabel = computed(() => {
    if (this.iconOnly()) {
      return undefined;
    }
    return this.commandTemplate()?.settings?.label;
  });

  // Track previous commandInput for comparison
  private previousCommandInput: CommandInput | undefined;

  constructor() {
    // Effect to react to commandName changes
    effect(() => {
      const name = this.commandName();
      if (name !== undefined) {
        this.loadCommandByName(name);
      }
    });

    // Effect to react to commandInput changes
    effect(() => {
      const input = this.commandInput();
      if (input !== undefined && !this.isCommandInputEqual(input, this.previousCommandInput)) {
        this.previousCommandInput = input;
        this.loadCommandByCommandInput(input);
      }
    });
  }

  private loadCommandByName(commandName: string): void {
    console.log("Loading command by name:", commandName);
    this.apolloService.query<fromGraphQl.CommandButtonQueryResponse>(
      fromGraphQl.COMMAND_BY_NAME_QUERY,
      { commandName }
    ).pipe(
      take(1),
      tap(response => {
        if (response.data.getCommand) {
          this.commandTemplate.set(new CommandTemplate(response.data.getCommand));
        }
      })
    ).subscribe();
  }

  private loadCommandByCommandInput(commandInput: CommandInput): void {
    this.apolloService.query<fromGraphQl.CommandButtonQueryResponse>(
      fromGraphQl.COMMAND_BY_COMMANDINPUT_QUERY,
      { commandInput }
    ).pipe(
      take(1),
      tap(response => {
        if (response.data.getCommandByCommandInput) {
          this.commandTemplate.set(new CommandTemplate(response.data.getCommandByCommandInput));
        }
      })
    ).subscribe();
  }

  private isCommandInputEqual(input1: CommandInput | undefined, input2: CommandInput | undefined): boolean {
    if (input1 === input2) return true;
    if (!input1 || !input2) return false;
    return JSON.stringify(input1) === JSON.stringify(input2);
  }

  getNativeElement(): HTMLElement {
    return this.elRef.nativeElement;
  }

  onClick(): void {
    this.commandService.execute(this.hasCommands(), this.commandTemplate(), this.reloadable());
  }
}
